import React from "react";
import { Stack } from "react-bootstrap";
import { Container, Row, Col, Spinner } from "reactstrap";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/CommonElements/PageTitle";
import { LabelValue } from "@/Component/Value";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { DATE_TIME_FORMAT } from "@/utils/constants";
import { useGetOneNusukCardsBatchQuery } from "@/services/nusukCards/nusukCards";
import { useSystem } from "@/utils/hooks/useLocation";

const NusukCardsBatchDetails = () => {
  const { t } = useTranslation();
  const { getLocalizedDate, getLocalizedText } = useLocalizedText();
  const { id } = useParams();
  const { currentSystem } = useSystem();
  const backLink = `/app/${currentSystem === "madina" ? "madina" : "makkah"}/nusuk-cards`;

  const { data, isLoading, isError } = useGetOneNusukCardsBatchQuery(id);
  const row = data?.data?.data ?? data?.data ?? null;

  if (isLoading) {
    return (
      <Stack gap={2}>
        <PageHeader title={t("VIEW")} backTo={backLink} />
        <Container fluid className="d-flex justify-content-center py-5">
          <Spinner />
        </Container>
      </Stack>
    );
  }

  if (isError || !row) {
    return (
      <Stack gap={2}>
        <PageHeader title={t("VIEW")} backTo={backLink} />
        <Container fluid>
          <p className="text-muted mb-3">
            {t("NO_DATA") || "No data found."}
          </p>
          <Link to={backLink} className="btn btn-primary">
            {t("BACK") || "Back"}
          </Link>
        </Container>
      </Stack>
    );
  }

  return (
    <Stack gap={2}>
      <PageHeader title={t("VIEW")} backTo={backLink} />
      <Container fluid>
        <div className="card border rounded-3">
          <div className="card-body py-3 px-4">
            <Row>
              <Col xs={12} md={6} lg={4} className="mb-3">
                <LabelValue
                  label={t("NUSUK_BATCH_NUMBER")}
                  value={row?.batch_no ?? "-"}
                  direction="vertical"
                />
              </Col>
              <Col xs={12} md={6} lg={4} className="mb-3">
                <LabelValue
                  label={t("CAMPAIGN_OR_GROUP_NUMBER")}
                  value={row?.entity_details?.number ?? row?.campaign_id ?? "-"}
                  direction="vertical"
                />
              </Col>
              <Col xs={12} md={6} lg={4} className="mb-3">
                <LabelValue
                  label={t("GROUP_NAME")}
                  value={row?.group_name ?? "-"}
                  direction="vertical"
                />
              </Col>
              <Col xs={12} md={6} lg={4} className="mb-3">
                <LabelValue
                  label={t("NUSUK_CARDS_COUNT")}
                  value={row?.card_no ?? "-"}
                  direction="vertical"
                />
              </Col>
              <Col xs={12} md={6} lg={4} className="mb-3">
                <LabelValue
                  label={t("NUSUK_REPRESENTATIVE_NAME")}
                  value={row?.representative_name ?? "-"}
                  direction="vertical"
                />
              </Col>
              <Col xs={12} md={6} lg={4} className="mb-3">
                <LabelValue
                  label={t("NUSUK_SAFE_NUMBER")}
                  value={row?.safe_number ?? "-"}
                  direction="vertical"
                />
              </Col>
              <Col xs={12} md={6} lg={4} className="mb-3">
                <LabelValue
                  label={t("NUSUK_BATCH_DATETIME")}
                  value={
                    row?.added_at
                      ? getLocalizedDate(row.added_at, DATE_TIME_FORMAT)
                      : "-"
                  }
                  direction="vertical"
                />
              </Col>
              <Col xs={12} md={6} lg={4} className="mb-3">
                <LabelValue
                  label={t("SERVICE_CENTER")}
                  value={
                    getLocalizedText(
                      row?.entity_details?.service_center?.name_ar,
                      row?.entity_details?.service_center?.name_en
                    ) ||
                    row?.entity_details?.service_center?.name ||
                    "-"
                  }
                  direction="vertical"
                />
              </Col>
              <Col xs={12} md={6} lg={4} className="mb-3">
                <LabelValue
                  label={t("NATIONALITY")}
                  value={
                    getLocalizedText(
                      row?.entity_details?.nationality?.name_ar,
                      row?.entity_details?.nationality?.name_en
                    ) ||
                    row?.entity_details?.nationality?.name ||
                    "-"
                  }
                  direction="vertical"
                />
              </Col>
              <Col xs={12} md={6} lg={4} className="mb-3">
                <LabelValue
                  label={t("STATUS")}
                  value={
                    Number(row?.delivery_status) === 1
                      ? t("NUSUK_STATUS_DELIVERED")
                      : Number(row?.delivery_status) === 0
                        ? t("NUSUK_STATUS_NOT_RECEIVED")
                        : "-"
                  }
                  direction="vertical"
                />
              </Col>
              <Col xs={12} md={6} lg={4} className="mb-3">
                <LabelValue
                  label={t("NUSUK_DELIVERY_DATE")}
                  value={
                    row?.delivery_date
                      ? getLocalizedDate(row.delivery_date, DATE_TIME_FORMAT)
                      : "-"
                  }
                  direction="vertical"
                />
              </Col>
              <Col xs={12} className="mb-3">
                <LabelValue
                  label={t("NOTES")}
                  value={row?.notes ?? "-"}
                  direction="vertical"
                />
              </Col>
            </Row>
          </div>
        </div>
      </Container>
    </Stack>
  );
};

export default NusukCardsBatchDetails;
